import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Languages } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { WIZARD } from "@/constants/testIds";
import DatePickerField from "@/components/wizard/DatePickerField";

const STYLES = ["pop", "rock", "rap", "electro", "metal", "classique", "reggae", "jazz", "country", "latino", "lofi"];
const MOODS = ["joyeux", "triste", "romantique", "motivant", "epique", "relaxant", "energique"];
const EVENTS = ["mariage", "anniversaire", "naissance", "retraite", "deces", "saint_valentin", "noel", "autre"];
const SONG_LANGUAGES = ["fr", "en", "es", "de", "it"];

export default function Step1StyleStory({ form, update }) {
  const { t } = useI18n();
  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
        <Label className="flex items-center gap-2 text-base">
          <Languages className="h-4 w-4 text-primary" />
          {t("wizard.songLanguageLabel")}
        </Label>
        <p className="mt-1 text-sm text-muted-foreground">{t("wizard.songLanguageDesc")}</p>
        <Select value={form.song_language} onValueChange={(v) => update({ song_language: v })}>
          <SelectTrigger data-testid={WIZARD.songLanguageSelect} className="mt-3 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SONG_LANGUAGES.map((lang) => (
              <SelectItem key={lang} value={lang}>{t(`wizard.lang_${lang}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-base">{t("wizard.styleLabel")}</Label>
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {STYLES.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={WIZARD.styleCard(s)}
              onClick={() => update({ style: s })}
              className={`rounded-xl border px-3 py-3 text-sm font-medium capitalize transition-all ${
                form.style === s ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
              }`}
            >
              {t(`options.style_${s}`)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-base">{t("wizard.moodLabel")}</Label>
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {MOODS.map((m) => (
            <button
              key={m}
              type="button"
              data-testid={WIZARD.moodCard(m)}
              onClick={() => update({ mood: m })}
              className={`rounded-xl border px-3 py-3 text-sm font-medium capitalize transition-all ${
                form.mood === m ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
              }`}
            >
              {t(`options.mood_${m}`)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="subject" className="text-base">{t("wizard.subjectLabel")}</Label>
        <Textarea
          id="subject"
          data-testid={WIZARD.subjectInput}
          placeholder={t("wizard.subjectPlaceholder")}
          value={form.subject}
          onChange={(e) => update({ subject: e.target.value })}
          className="mt-3 min-h-28 rounded-xl"
        />
      </div>

      <div className="rounded-2xl border border-border bg-card/50 p-5">
        <Label className="text-base">{t("wizard.personTitle")}</Label>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            data-testid={WIZARD.personFirstName}
            placeholder={t("wizard.firstNameLabel")}
            value={form.person_first_name}
            onChange={(e) => update({ person_first_name: e.target.value })}
            className="rounded-xl"
          />
          <Input
            data-testid={WIZARD.personLastName}
            placeholder={t("wizard.lastNameLabel")}
            value={form.person_last_name}
            onChange={(e) => update({ person_last_name: e.target.value })}
            className="rounded-xl"
          />
          <DatePickerField
            testId={WIZARD.personDate}
            value={form.person_date}
            onChange={(v) => update({ person_date: v })}
            placeholder={t("wizard.dateLabel")}
          />
          <Select value={form.event_type} onValueChange={(v) => update({ event_type: v })}>
            <SelectTrigger data-testid={WIZARD.eventSelect} className="rounded-xl">
              <SelectValue placeholder={t("wizard.eventLabel")} />
            </SelectTrigger>
            <SelectContent>
              {EVENTS.map((ev) => (
                <SelectItem key={ev} value={ev}>{t(`options.event_${ev}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
